import { tokenStore } from "@/lib/api";

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

/**
 * Downloads a file from an authenticated endpoint.
 *
 * A plain anchor cannot do this: the browser would issue the request without the bearer
 * token and get a 401, and the token is not something to put in a query string where it
 * would land in server logs and browser history. So the bytes are fetched, turned into a
 * blob URL, and handed to a synthetic link.
 */
export async function downloadExport(path: string, fallbackName: string): Promise<void> {
  const tokens = tokenStore.read();

  const response = await fetch(`${BASE_URL}${path}`, {
    headers: tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {},
  });

  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? "There is nothing to export yet."
        : "The export could not be generated.",
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileNameFrom(response) ?? fallbackName;

    document.body.append(link);
    link.click();
    link.remove();
  } finally {
    // Released on the next tick: revoking synchronously can cancel the download in
    // some browsers before it has read the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

/** The server's own filename, so the date and ring number it chose survive. */
function fileNameFrom(response: Response): string | null {
  const disposition = response.headers.get("Content-Disposition");
  const match = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);

  return match?.[1] ? decodeURIComponent(match[1]) : null;
}
